/* eslint-disable react-func/max-lines-per-function */
/* eslint-disable max-lines */
import React, { useEffect, useRef, useState, ClipboardEvent } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Form, Button, Checkbox, Popover, theme, Flex } from '@fuxi/eevee-ui';
import { UploadFile } from 'antd/lib';
import dayjs from 'dayjs';
import { InfoCircleFilled, PlusOutlined } from '@ant-design/icons';
import { useLocalStorageState, useDrop, useSize, useLoginInfo } from '@fuxi/eevee-hooks';
import { throttle, isEmpty } from 'lodash-es';
import { useQueryClient } from '@tanstack/react-query';
import { ReactImageEditor, ImagePlugin, findPluginByName, EditorMode } from '@fuxi/react-img-editor';
import { sendEvent } from 'common/tracker';
import { CustomEvent } from 'common/tracker/custom-event';
import useAntStaticComponent from 'common/hooks/useAntStaticComponent';

import { ExportFileType, TaskMode } from 'common/types';
import MyIcon from '@/components/MyIcon';
import AddComment from '@/components/AddComment';
import service from '@/service';
import { CommentSourceType } from '@/types/comment';
import { useTaskEditorContext } from '@/context/TaskEditorContext';
import { useTaskDetailContext } from '@/context/TaskDetailContext';
import { blobToFile } from '@/utils';
import { useAppSelector, useUserAccount } from '@/hooks';
import { selectCurVersion } from '@/store/version';

import '@fuxi/react-img-editor/style.css';

import cx from './index.module.css';

const CACHE_INTERVAL = 5;

type CanvasSize = {
  width: number;
  height: number;
};

export const MAX_CANVAS_SIZE = 20000;

const CommentTool: React.FC = () => {
  const { message } = useAntStaticComponent();
  const { token } = theme.useToken();
  const { rbacUserInfo } = useLoginInfo();
  const { userAccount } = useUserAccount();
  const taskDetailInfo = useTaskDetailContext();
  const {
    setMode,
    id,
    versionId,
    setPreviewType,
    setCurVersion,
    cachedPreviewType,
    annotation,
    setAnnotation,
    getCachedComment,
    syncCommentInfo,
    clearCachedComment,
    modelViewerHide,
    setModelViewerHide,
    previewType,
    miniSnapShot,
    setMiniSnapShot,
    setDisableQuery,
    fileList,
    currentIndex,
    exportFile,
    setEditorMode,
  } = useTaskEditorContext();
  const { projectCode, requirementType, stage, roleInfo } = useTaskDetailContext();
  const queryClient = useQueryClient();
  const stageRef = useRef<any>();
  const commentToolRef = useRef<any>();
  const commentToolSize = useSize(commentToolRef);
  const uploadPluginRef = useRef<any>(new ImagePlugin());
  const drawParamsRef = useRef<any>();
  const saveTimerRef = useRef<any>();
  const curVersion = useAppSelector(selectCurVersion);

  const [mainImageObj, setMainImageObj] = useState<HTMLImageElement | undefined>();
  const [defaultPluginName, setDefaultPluginName] = useState('pen');
  const [defaultStage, setDefaultStage] = useState();
  const [defaultImages, setDefaultImages] = useState<Record<string, string>>({});
  const [isEnable, setIsEnable] = useState(false);
  const [canvasSize, setCanvasSize] = useState<CanvasSize>();
  const [imgWidth, setImgWidth] = useState(0);
  const [imgHeight, setImgHeight] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);

  const [initialContent, setInitialContent] = useState(getCachedComment()?.content || '');
  const [isCommentToolShow, setIsCommentToolShow] = useState(false);
  const [attachment, setAttachment] = useState<Array<UploadFile>>([]);
  const [loading, setLoading] = useState(false);
  const [isAttachmentUploading, setIsAttachmentUploading] = useState(false);

  const [ignoreCommentAlert, setIgnoreCommentAlert] = useLocalStorageState<boolean>('eeveeCommentIgnore', {
    defaultValue: false,
  });

  const [commentForm] = Form.useForm();
  const [isAlertShow, setIsAlertShow] = useState(!ignoreCommentAlert);
  const asyncServiceFn = (file: File) => {
    return service.upload.uploadFileByProxy(file, 'TASK_MATERIAL_LIBRARY', false);
  };

  const handleImagePick = async (file: File) => {
    try {
      const curDrawParams = drawParamsRef.current();
      uploadPluginRef.current.onUploaded(
        {
          ...curDrawParams,
          refImage: {
            file,
            createTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            createUser: rbacUserInfo.nickName || '',
          },
        },
        asyncServiceFn,
      );
    } catch (err) {
      console.error(err);
    }
  };

  function setStage(stage) {
    stageRef.current = stage;
  }
  const getDrawEventParams = (params) => {
    drawParamsRef.current = params;
  };

  const onCancel = () => {
    // setFileLoading(true);
    //清空画布
    setIsEnable(false);
    setMode(TaskMode.Preview);
    setPreviewType(cachedPreviewType);
    setModelViewerHide(false);
    clearCachedComment();
    setMiniSnapShot(null);
    setDisableQuery(false);

    if (saveTimerRef.current) {
      clearInterval(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    setEditorMode(EditorMode.ANNOTATION);
  };

  const onAddError = () => {
    message.error('新建反馈失败');
    onCancel();
  };

  const onEditorSave = throttle(async ({ content, attachment }) => {
    try {
      setLoading(true);
      const indexFileKey = curVersion?.qualityInspection?.dccVersion ? 'uuid' : 'id';
      if (drawParamsRef.current()) {
        const stage = stageRef.current;
        const drawParams = drawParamsRef.current();
        drawParams.handlePluginChange(null);
        drawParams.blurPalette();
        // drawParams.onAspectChange('aspect-fit');
        stage.x(0);
        stage.y(0);
        stage.scaleX(1);
        stage.scaleY(1);

        setTimeout(async () => {
          //有开启绘图的情况
          let data = stage.getClientRect();
          let blob;

          if (data.width > MAX_CANVAS_SIZE || data.height > MAX_CANVAS_SIZE) {
            //把宽或者高等比例缩小到最大值
            const scale = Math.min(MAX_CANVAS_SIZE / data.width, MAX_CANVAS_SIZE / data.height);
            stage.scaleX(scale);
            stage.scaleY(scale);
            stage.draw();
            data = stage.getClientRect();
          }
          // 延迟下载，等触发 plugin 的 onLeave 生命周期，清除未完成的现场
          const conf = {
            imageSmoothingEnabled: false,
            x: data.x,
            y: data.y,
            width: data.width,
            height: data.height,
          };
          blob = await stageRef.current.toBlob(conf);

          if (!blob) {
            setLoading(false);
            message.error('截图失败，请重试');
            return;
          }
          const newImageFile = blobToFile(blob, `${uuidv4()}.png`, blob.type);
          //上传文件
          const downloadUrl = await service.upload.uploadFileByProxy(newImageFile, 'TASK_MATERIAL_LIBRARY', false);
          const mainImage = drawParams.mainImage;
          const images = Object.fromEntries(drawParams.imageMap || new Map());
          const obj = {
            content: content,
            preview: downloadUrl || '',
            images: images,
            annotation: {
              mainImage: mainImage,
              stage: stage.toObject(),
            },
            attachment: attachment.map((item) => {
              return { url: item.url, fileName: item.name };
            }),
            assetPosition: {
              fileId: fileList[currentIndex][indexFileKey],
              url: exportFile ? (Array.isArray(exportFile) ? exportFile[0] : exportFile) : '',
              fileName: fileList[currentIndex].fileName,
              takeIndex: 0,
              frameIndex: 0,
              snapShot: {},
              playerTransform: {
                scale: 1,
                transX: 1,
                transY: 1,
                width: 0,
                height: 0,
              },
            },
          };
          const { code, message: msg } = await service.comments.addReview(versionId, id, obj, CommentSourceType.Normal);
          const isSuc = code === 0;
          if (isSuc) {
            setLoading(false);
            message.success('新建反馈成功');
            // 更新本地的反馈数量
            queryClient.refetchQueries(['getReviewList']);
            service.exportVersions.getTaskCurrentVersionFile(versionId).then((data) => {
              setCurVersion({ ...data });
            });
            onCancel();
          } else {
            throw new Error(msg);
          }
        });
      }
    } catch (err) {
      console.error(err);
      message.error(JSON.stringify(err));
      setLoading(false);
      onAddError();
    }
  }, 500);

  const handlePaste = throttle(async (e: ClipboardEvent<HTMLInputElement>) => {
    try {
      const target = e.target as HTMLElement;
      if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') {
        return;
      }
      const items = e.clipboardData && e.clipboardData.items;
      if (items && items.length) {
        let isValid = false;
        /* eslint-disable max-depth */
        for (let i = 0; i < items.length; i++) {
          if (items[i].kind === 'string') {
            isValid = true;
          } else if (items[i].kind === 'file' && items[i].type.match('^image/')) {
            // copy data item as an image file
            const f = items[i].getAsFile() as File;
            isValid = true;
            changePlugin('image', false);
            setTimeout(async () => {
              //等待内部状态切换完毕
              await handleImagePick(f);
            }, 500);
          }
        }
        if (!isValid) {
          message.error('仅支持粘贴图片类型文件');
        }
      } else {
        throw new Error('当前浏览器不支持访问剪贴板');
      }
    } catch (e: any) {
      message.error(e.message);
    }
  }, 500);

  const changePlugin = (pluginName: string, trigger = true) => {
    if (!drawParamsRef.current) {
      return;
    }
    const { plugins, handlePluginChange } = drawParamsRef.current();
    const plugin = findPluginByName(plugins, pluginName);
    handlePluginChange(plugin, trigger);
  };
  const generateCacheTimer = () => {
    const timer = setInterval(() => {
      //有开启绘图的情况
      const stage = stageRef.current.toObject();
      const drawParams = drawParamsRef.current();
      const mainImage = drawParams.mainImage;
      const images = Object.fromEntries(drawParams.imageMap || new Map());
      const content = commentForm.getFieldValue('content');

      const commentWithTTL = {
        content: content,
        preview: '',
        annotation: {
          mainImage: mainImage,
          stage,
          images: images,
          pluginName: drawParams.currentPlugin ? drawParams.currentPlugin.name : '',
        },
        createUser: userAccount?.nickName || '',
        createTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        taskId: id,
        version: versionId,
        ttl: 24 * 60 * 60, //单位是秒

        // attachment,
      };
      syncCommentInfo(commentWithTTL);
    }, CACHE_INTERVAL * 1000);
    return timer;
  };

  useEffect(() => {
    const uploadFromLocal = async () => {
      try {
        const arrFileHandle = await window.showOpenFilePicker({
          types: [
            {
              description: 'Images',
              accept: {
                'image/*': ['.png', '.gif', '.jpeg', '.jpg', '.webp', '.bmp', '.svg'],
              },
            },
          ],
          excludeAcceptAllOption: true,
          multiple: false,
        });

        // 遍历选择的文件
        for (const fileHandle of arrFileHandle) {
          // 获取文件内容
          const fileData = await fileHandle.getFile();
          await handleImagePick(fileData);
        }
      } catch (err) {
        console.error(err);
      }
    };
    ImagePlugin.setUploadHandler(uploadFromLocal);
    const pasteHandler = (e) => {
      handlePaste(e);
    };
    document.addEventListener('paste', pasteHandler);
    return () => {
      // remove all events
      const drawParams = drawParamsRef.current();
      if (drawParams) {
        drawParams.removeEvents(true);
      }
      setMainImageObj(undefined);
      setAnnotation({ mainImage: '', stage: undefined, images: {}, pluginName: 'pen' });
      document.removeEventListener('paste', pasteHandler);
    };
  }, []);

  useEffect(() => {
    if (annotation.mainImage) {
      const imageObj = new Image();
      imageObj.onload = () => {
        setMainImageObj(imageObj);

        if (annotation.stage) {
          setDefaultStage(annotation.stage);
          if (annotation.images && !isEmpty(annotation.images)) {
            setDefaultImages(annotation.images);
          }
          if (annotation.pluginName) {
            setDefaultPluginName(annotation.pluginName);
          }
        }
        setCanvasSize({
          width: commentToolRef.current.clientWidth,
          height: commentToolRef.current.clientHeight,
        });

        if (cachedPreviewType !== ExportFileType.Image) {
          setImgWidth(imageObj.naturalWidth);
          setImgHeight(imageObj.naturalHeight);
        } else {
          setImgWidth(imageObj?.width || 0);
          setImgHeight(imageObj?.height || 0);
        }
        setIsEnable(true);
        if (saveTimerRef.current) {
          clearInterval(saveTimerRef.current);
        }
        // 开启定时保存到localstorage
        saveTimerRef.current = generateCacheTimer();
      };
      imageObj.crossOrigin = 'use-credentials';

      imageObj.src = `${annotation.mainImage}?t=${new Date().getTime()}`;
    } else {
      setCanvasSize({
        width: commentToolRef.current.clientWidth,
        height: commentToolRef.current.clientHeight,
      });
      setIsEnable(true);
    }
    return () => {
      if (saveTimerRef.current) {
        clearInterval(saveTimerRef.current);
      }
    };
  }, [annotation]);

  useEffect(() => {
    if (miniSnapShot) {
      changePlugin('image', false);
      setTimeout(async () => {
        //等待内部状态切换完毕
        await handleImagePick(miniSnapShot);
      }, 500);
    }
  }, [miniSnapShot]);

  useDrop(commentToolRef, {
    onFiles: (files, e) => {
      if (files[0].type.match('^image/')) {
        changePlugin('image', false);
        setIsDragOver(false);
        setTimeout(async () => {
          //等待内部状态切换完毕
          await handleImagePick(files[0]);
        }, 500);
      } else {
        setIsDragOver(false);
        message.error('仅支持引用图片类型文件');
      }
    },
    onUri: (uri, e) => {
      console.log(`uri: ${uri} dropped`);
      setIsDragOver(false);
    },
    onDom: (content: string, e) => {
      console.log(`custom: ${content} dropped`);
      setIsDragOver(false);
    },
    onDragEnter: () => setIsDragOver(true),
    onDragLeave: () => setIsDragOver(false),
  });
  const toggleShowChildren = () => {
    if (drawParamsRef.current()) {
      const drawParams = drawParamsRef.current();
      drawParams.handlePluginChange(null);
    }
    setIsCommentToolShow(!isCommentToolShow);
  };

  const toggleShowModel = () => {
    setModelViewerHide(!modelViewerHide);
  };

  //发布反馈
  const createComment = async () => {
    if (isAttachmentUploading) {
      message.warning('附件上传中，请稍后');
      return;
    }
    const fields = commentForm.getFieldsValue();
    Object.assign(fields, { attachment });
    onEditorSave(fields);
  };
  const onExit = () => {
    //清空表单
    commentForm.resetFields();
    onCancel();
  };
  const prefixRender = () => {
    return (
      <Popover
        autoAdjustOverflow={true}
        open={isAlertShow}
        trigger={['click']}
        onOpenChange={(open: boolean) => closeAlert()}
        overlayClassName={cx('comment-tool-ant-popover')}
        content={
          <div>
            <InfoCircleFilled style={{ color: token.colorPrimary }} />
            <span className={cx('comment-tool-alert-text')}>点击icon，可展开富文本</span>
            <Checkbox
              checked={ignoreCommentAlert}
              onChange={(e) => setIgnoreCommentAlert(e.target.checked)}
              className={cx('comment-tool-ant-checkbox')}>
              下次不再提醒
            </Checkbox>
            <Button type="link" onClick={closeAlert}>
              我知道了
            </Button>
          </div>
        }>
        <span
          key="icon-expand-text"
          className={cx('toolbar-icon', isCommentToolShow && 'toolbar-icon-activated')}
          title={isCommentToolShow ? '收起' : '展开'}
          onClick={toggleShowChildren}>
          {isCommentToolShow ? (
            <MyIcon icon="shouqi" className={'icon-expand-text'} size="24px" />
          ) : (
            <MyIcon icon="zhankai" className={'icon-expand-text'} size="24px" />
          )}
        </span>
      </Popover>
    );
  };
  const sendTracker = (data: any) => {
    sendEvent(CustomEvent.UseCommentTool, {
      et: 'CLK',
      c1: projectCode,
      c2: roleInfo?.originRole,
      c3: curVersion?.parentTaskId, // 父任务id
      c4: curVersion?.uniqueId, //阶段id
      c5: data?.name ?? '',
    });
  };
  const suffixRender = () => {
    return (
      <>
        {previewType === ExportFileType.Babylon && (
          <span
            key="view-model"
            className={cx('toolbar-icon')}
            onClick={toggleShowModel}
            title={modelViewerHide ? '查看模型' : '收起模型'}>
            {modelViewerHide ? <MyIcon icon="view-box" size={24} /> : <MyIcon icon="shouqimoxing" size={24} />}
          </span>
        )}
        <Flex gap="8px" alignCenter>
          <span key="cancel-comment">
            <Button
              type="default"
              onClick={onExit}
              className={cx('custom-render-toolbar-btn')}
              style={{ borderColor: token.colorPrimary, color: token.colorPrimary }}>
              取消反馈
            </Button>
          </span>
          <span key="create-comment">
            <Button
              type="primary"
              onClick={createComment}
              loading={loading}
              className={cx('custom-render-toolbar-btn')}
              style={{ borderColor: token.colorPrimary }}>
              发布反馈
            </Button>
          </span>
        </Flex>
      </>
    );
  };

  const closeAlert = () => {
    setIsAlertShow(false);
  };

  return (
    // <Spin spinning={loading} size="large">
    <div className={cx('comment-tool')}>
      <div className={cx('comment-tool-main')} ref={commentToolRef}>
        {isDragOver && (
          <div className={cx('comment-tool-main-modal')}>
            <div className={cx('comment-tool-main-info')}>
              <PlusOutlined /> <span className={cx('comment-tool-main-text')}>引用当前图片</span>
            </div>
          </div>
        )}
        {isEnable && (
          <ReactImageEditor
            imgWidth={imgWidth}
            imgHeight={imgHeight}
            defaultPluginName={defaultPluginName}
            imageObj={mainImageObj}
            width={canvasSize?.width}
            height={canvasSize?.height}
            plugins={[uploadPluginRef.current]}
            showChildren={isCommentToolShow}
            toolbar={{
              items: {
                plugins: ['|', 'zoom', 'pen', 'rect', 'circle', 'arrow', 'text', 'eraser', 'image', '|'],
                others: ['|', 'repeal', 'redo', '|'],
              },
            }}
            getStage={setStage}
            showToolbar={true}
            crossOrigin="use-credentials"
            setDrawEventParams={getDrawEventParams}
            defaultStage={defaultStage}
            images={defaultImages}
            prefixRender={prefixRender}
            suffixRender={suffixRender}
            mode={EditorMode.PALETTE}
            domScale={{ x: 1, y: 1 }}
            defaultScale={1}
            paletteStyle={{ background: 'rgba(10,14,26,1)' }}
            style={{ background: token.colorBgMask, borderRadius: '8px' }}
            maxWidth={16000}
            maxHeight={16000}
            onPluginChange={sendTracker}>
            <AddComment
              maxCount={Number(taskDetailInfo.reviewAttachmentFileLimit)}
              maxSize={Number(taskDetailInfo.reviewAttachmentSizeLimit)}
              isShow={isCommentToolShow}
              commentForm={commentForm}
              content={initialContent}
              attachment={attachment}
              setAttachment={setAttachment}
              setIsUploading={setIsAttachmentUploading}
              width={820}
            />
          </ReactImageEditor>
        )}
      </div>
    </div>
    // </Spin>
  );
};
export default CommentTool;
